import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { debounceTime, finalize, map, mergeMap, of, Subject, Subscription, timer } from 'rxjs';
import { ICoreAuthorizationDTO, ICoreMemberDTO, IDocumentResponseDTO, WidgetConfiguration } from '@core/model';
import { emailRegexp } from '@core/constants';
import { AuthenticationService, NotificationService } from '@core/services';
import { PlatformService } from '@core/services/context/platform.service';
import {
  authorizationContextFactory,
  AuthorizationContextService,
  documentContextFactory,
  DocumentsContextService,
  documentsMoveContextFactory,
  DocumentsMoveContextService,
  documentsReferencesContextFactory,
  DocumentsReferencesContextService,
  documentsUploadContextFactory,
  DocumentsUploadContextService,
  documentsUploadLetterContextFactory,
  DocumentsUploadLetterContextService,
  ENotificationReportContextFactory,
  ENotificationReportContextService,
  memberContextFactory,
  MemberContextService
} from '@core/services/context/factory';
import { HttpClient } from '@angular/common/http';
import { ShellService } from '@mynexus/mfe-core';
import { UserCredentials } from '@core/model';
import { ProductNames } from '@mynexus/mynexus-ui-lib';
import { ContinuedStayRouterEvents } from '@app/documents/models';
import { PlatformHttpService } from '@app/core/services/platform.http.service';
import { AdditionalProps } from '@core/model/core.model';
import { ENotification } from '@app/core/model/e-Notification';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentService } from '@app/core/services/document.service';

@Component({
  selector: 'app-documents-widget',
  templateUrl: './documents-widget.component.html',
  styleUrls: ['./documents-widget.component.scss'],
  providers: [
    PlatformService,
    {
      provide: DocumentsContextService,
      useFactory: documentContextFactory,
      deps: [HttpClient, ShellService]
    },
    {
      provide: AuthorizationContextService,
      useFactory: authorizationContextFactory,
      deps: [HttpClient, ShellService]
    },
    {
      provide: MemberContextService,
      useFactory: memberContextFactory,
      deps: [HttpClient, ShellService]
    },
    {
      provide: DocumentsMoveContextService,
      useFactory: documentsMoveContextFactory,
      deps: [HttpClient, ShellService, AuthenticationService]
    },
    {
      provide: DocumentsUploadLetterContextService,
      useFactory: documentsUploadLetterContextFactory,
      deps: [HttpClient, ShellService]
    },
    {
      provide: DocumentsUploadContextService,
      useFactory: documentsUploadContextFactory,
      deps: [HttpClient, ShellService]
    },
    {
      provide: DocumentsReferencesContextService,
      useFactory: documentsReferencesContextFactory,
      deps: [HttpClient, ShellService]
    },
    {
      provide: ENotificationReportContextService,
      useFactory: ENotificationReportContextFactory,
      deps: [HttpClient, ShellService]
    }
  ],
  standalone: false
})
export class DocumentsWidgetComponent implements OnChanges {
  @HostBinding('class') class = 'documents-web-component';

  // Catch custom event triggered from HH Clinical Responses when some documents were checked
  @HostListener('window:reloadDocuments')
  onReloadDocuments(): void {
    this.getDocuments();
  }

  @Input() collapsed: boolean;
  @Input() authorizationByProductId: string;
  @Input() authorizationNumber: string;

  @Input() userEmail: string;
  @Input() tenantId: string;
  @Input() isExternal: boolean;
  @Input() lineOfBusinessId: string;


  @Input() set configuration(value: string) {
    try {
      const { context, features, lineOfBusiness }: WidgetConfiguration = JSON.parse(value);
      this.platformService.widgetContext = context;
      this.platformService.widgetFeatures = features;
      this.platformService.lineOfBusiness = lineOfBusiness;
    } catch (e) {
      console.log('Invalid configuration passed');
    }
  }

  @Input() memberString!: string;

  @Input() set authorizationString(value: string) {
    if (value) {
      this.authorization = JSON.parse(value);
      if (this.platformService.widgetContext === ProductNames.HomeHealth) {
        this.authorizationDto = this.authorizationContextService.mapAuthorizationDTO(this.authorization);
      }
    }
  }

  @Input() set servicingProviderString(value: string) {
    if (value) {
      this.servicingProvider = JSON.parse(value);
    }
  }

  @Input() set orderingProviderString(value: string) {
    if (value) {
      this.orderingProvider = JSON.parse(value);
    }
  }

  @Input() tenantName: string;

  // TODO: Merge line data with additional props
  @Input() set lineDataString(value: string) {
    if (value) {
      this.lineData = JSON.parse(value);
    }
  }

  // Any additional properties that other streams need can be added here with the understanding
  // that we observe and assess the necessity of it. Ideally we will reach a point where we can
  // remove this completely once we are fully aligned.
  @Input() set additionalPropsString(value: string) {
    if (value) {
      this.additionalProps = JSON.parse(value);
    }
  }

  @Output() openNote = new EventEmitter<string>();
  @Output() closeNote = new EventEmitter<void>();
  @Output() openDocument = new EventEmitter<IDocumentResponseDTO[]>();
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() sendFaxCompleted = new EventEmitter<void>();
  @Output() routerEvents = new EventEmitter<ContinuedStayRouterEvents>();
  authorization: any; // To be replaced with authorizationDto
  authorizationDto: ICoreAuthorizationDTO;
  memberDto: ICoreMemberDTO;
  servicingProvider: any;
  orderingProvider: any;
  lineData: object[];
  additionalProps: AdditionalProps;
  documents: IDocumentResponseDTO[] = [];
  activeTabIndex = 0;
  eNotifications: ENotification[] = null;
  isLoading = true;
  fileSubscription!: Subscription;
  // In Angular 21 web components, ngOnChanges fires per-property rather than in bulk.
  // debounceTime(0) defers execution to the next macrotask, allowing all sibling inputs
  // (e.g. tenantId, authorizationNumber) to settle before the API calls are made.
  private readonly _triggerLoad$ = new Subject<void>();

  // Same batching concern applies to DTO initialization: configuration and memberString
  // may arrive in separate ngOnChanges cycles, so we defer mapping until both are present.
  private readonly _triggerInit$ = new Subject<void>();

  readonly platformService = inject(PlatformService);
  private readonly authorizationContextService = inject(AuthorizationContextService);
  private readonly memberContextService = inject(MemberContextService);
  private readonly documentsContextService = inject(DocumentsContextService);
  private readonly notificationService = inject(NotificationService);
  private readonly ref = inject(ChangeDetectorRef);
  private readonly platformHttpService = inject(PlatformHttpService);
  private readonly eNotificationReportService = inject(ENotificationReportContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly documentService = inject(DocumentService);

  constructor() {
    this._triggerInit$.pipe(
      debounceTime(0),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.authorizationDto = this.authorizationContextService.mapAuthorizationDTO(this.authorization);
      if (this.memberString) {
        const member = JSON.parse(this.memberString);
        this.memberDto = this.memberContextService.mapMemberDTO(member);
      }
    });

    this._triggerLoad$.pipe(
      debounceTime(0),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.getDocuments();
      this.getENotification();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['configuration']?.firstChange || changes['memberString']) {
      this._triggerInit$.next();
    }

    if (changes['authorizationByProductId']) {
      this._triggerLoad$.next();
    }
  }

  getDocuments() {
    this.documentsContextService
      .getFilesMetaData(
        this.tenantId,
        this.authorizationByProductId,
        this.authorizationNumber
      )
      .pipe(
        mergeMap((documents: IDocumentResponseDTO[] | IDocumentResponseDTO) => {
          if (!Array.isArray(documents)) {
            documents = [documents];
          }
          return this.setUserCredentials(documents);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading = false),
      )
      .subscribe({
        next: (response: IDocumentResponseDTO[]) => {
          this.documents = response;
          this.documentService.combineAndSetDocuments(response, this.lineData, this.platformService.widgetContext);
        },
        error: () => {
          this.notificationService.showError();
          this.documents = [];
        }
      });
  }

  setUserCredentials(documents: IDocumentResponseDTO[]) {
    const emailList = documents.reduce((acc, cur) => {
      if (cur.createdBy && !acc.includes(cur.createdBy as never) && emailRegexp.test(cur.createdBy)) {
        acc.push(cur.createdBy as never);
      } else if (cur.createdBy === 'Automated Letter Creation' || cur.createdBy === 'mynadmin') { // according to story 83478
        cur.createdBy = 'Portal';
      }
      return acc;
    }, []);

    if (emailList.length) {
      return this.platformHttpService.getUserProfileCredentialsByEmails(emailList).pipe(
        map((credentialsList: UserCredentials[]) => {
          if (credentialsList) {
            documents = documents.map((document: IDocumentResponseDTO) => {
              const userCredentials = credentialsList.find(credentials => credentials.email === document.createdBy);

              if (userCredentials) {
                let creds = '';
                if (userCredentials.credentials) {
                  creds = ', ' + userCredentials.credentials;
                }
                document.createdByEmail = document.createdBy;
                // Set display value for CreatedBy column
                document.createdBy = userCredentials.isExternal ? 'External' : `${userCredentials.firstName} ${userCredentials.lastName}${creds}`;
                document.isExternal = userCredentials.isExternal;
              }

              return document;
            });
          }
          return documents;
        })
      );
    } else {
      return of(documents);
    }
  }

  // TODO: Remove PAC Commercial after LOB migration
  closeNotesOnStreamCheck(): void {
    if (this.platformService.widgetContext === ProductNames.PACIM || this.platformService.widgetContext === ProductNames.PacCommercial) {
      this.closeNotes();
    }
  }

  /**
   * Wait for DocumentBuilder to create document and then refresh documents list
   */
  goToDocsWithDelay() {
    this.activeTabIndex = 0;
    timer(2000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getDocuments();
      this.sendFaxCompleted.emit();
    });
  }

  /**
   * Wait for DocumentBuilder to create appeal packet and then refresh documents list
   */
  refreshDocsAfterAppealPacket() {
    this.activeTabIndex = 0;
    timer(3000).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.getDocuments();
    });
  }

  openNotes(noteCategory: string): void {
    this.openNote.emit(noteCategory);
  }

  closeNotes(): void {
    this.closeNote.emit();
  }

  openDocuments(documentType: IDocumentResponseDTO[]): void {
    this.openDocument.emit(documentType);
  }

  uploadLetterComplete(): void {
    this.onDocumentUpload();
    this.ref.markForCheck();
  }

  // TODO: Remove PAC Commercial after LOB migration
  goToAndRefreshDocs() {
    if (this.platformService.widgetContext === ProductNames.PACIM || this.platformService.widgetContext === ProductNames.PacCommercial) {
      this.closeNotes();
    }
    this.activeTabIndex = 0;
    this.getDocuments();
  }

  updateDocumentReceivedTime(updatedDocument: IDocumentResponseDTO): void {
    // replace value in documents array with update document, no need to fully fetch the whole list.
    const indexToUpdate = this.documents.findIndex(item => item.docMetaDataId === updatedDocument.docMetaDataId);
    this.documents[indexToUpdate].dateTimeFaxReceived = updatedDocument.dateTimeFaxReceived;

    // Assign object to trigger change detection
    this.documents = [...this.documents];
  }

  getENotification(): void {
    if (this.platformService.widgetFeatures.eNotification) {
      this.eNotificationReportService.getENotificationReport(this.tenantId, this.authorizationByProductId)
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        re => {
          this.eNotifications = re;
        }
      );
    }
  }

  onDocumentUpload(): void {
    this.notificationService.showSuccess('Documents uploaded successfully');
    this.goToAndRefreshDocs();
    this.ref.markForCheck();
  }
}
